import { Injectable } from '@angular/core';
import { Search } from '@local/client-contracts';
import { ItemsCache } from 'src/app/bar/services/items-cache';
import { ItemsCacheService } from 'src/app/bar/services/items-cache.service';
import { map, type Observable, of } from 'rxjs';
import type { MessageResource } from '@local/client-contracts/src/chats';
import type { Command } from '@local/client-contracts/src/commands';
import type { ExternalSourceItem } from '@local/client-contracts/src/search';
import { partition } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class AnswerResourceAgenticService {
  private readonly ID_KEY_CACHE = 'resource.externalId';
  private readonly CACHE_TYPE = 'chat-resources';
  private cacheResources: ItemsCache<Search.ResultResourceItem>;
  private _sources: { [id: string]: Search.ExternalSourceItem } = {};

  constructor(private itemsCacheService: ItemsCacheService) {
    this.cacheResources = this.itemsCacheService.getItemsCache(this.CACHE_TYPE);
  }

  async updateResourceInCache(resources: (Search.ResultResourceItem | Search.ExternalSourceItem | MessageResource)[]) {
    const [external, internal] = partition(resources, (r) => r.type === 'external-source');

    external.forEach((r) => {
      if (r.id) {
        this._sources[r.id] = r;
      }
    });
    this.cacheResources.updateItemsInCache(internal as Search.ResultResourceItem[], this.ID_KEY_CACHE);
  }

  getResourceById$(id: string): Observable<Search.ResultResourceItem | Search.ExternalSourceItem> {
    if (this._sources[id]) {
      return of(this.buildExternalSource(this._sources[id]));
    } else {
      return this.cacheResources.getItemsByIds$([id]).pipe(
        map((items) => {
          return items.length ? items[0] : null;
        })
      );
    }
  }

  getResources$(resources: (Search.ExternalSourceItem | MessageResource)[]): Observable<Search.ResultItem[]> {
    const internalIds = resources.filter((r) => r.type !== 'external-source').map((r) => r.externalId);
    return this.cacheResources.getItemsByIds$(internalIds).pipe(
      map((items) => {
        return resources.map((r) => {
          if (r.type === 'external-source') {
            return this.buildExternalSource(this._sources[r.id]);
          }
          const item = items.find((item) => item.resource.externalId === r.externalId);
          const verificationStatus = r.verificationStatus;
          if (item) {
            item.snippet = r.snippet;
            item.resource.data.verificationStatus = verificationStatus;
          }
          return item;
        });
      })
    );
  }

  private buildExternalSource(source: ExternalSourceItem): ExternalSourceItem {
    return {
      type: 'external-source',
      kind: 'external-source',
      ...source,
      view: {
        linkName: source.siteName,
        title: {
          text: source.title || source['name'],
          onClick: {
            url: source.url,
            type: 'open-url',
          } as Command,
        },
        subtitle: {
          text: source.description,
        },
        icon: {
          lightUrl: source.icon,
        },
      },
    };
  }

  async resetCache() {
    this.cacheResources.resetCache();
    this._sources = {};
  }
}
